import { useState } from 'react';

import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { SubscribePublicationsModal } from './SubscribePublicationsModal';

interface SubscribePublicationsTriggerProps {
    /**
     * `pill` is the dark call to action on white sections; `footer` is the
     * translucent pill in the site footer; `link` is a plain text button for
     * dense chrome.
     */
    variant?: 'pill' | 'footer' | 'link';
    label?: string;
    className?: string;
    /** Fired when a subscription lands, for hosts keyed on subscription status. */
    onSubscribed?: () => void;
}

/**
 * Opens the publication subscribe modal, and hosts it.
 *
 * The surfaces this replaces used to subscribe on submit, which is what put
 * people on every marketing list at once. The field now opens the chooser
 * instead, so the address is collected in the same place as the choice.
 */
export const SubscribePublicationsTrigger = ({
    variant = 'pill',
    label = 'Subscribe',
    className,
    onSubscribed,
}: SubscribePublicationsTriggerProps) => {
    const [open, setOpen] = useState(false);
    /**
     * The modal is not mounted until it is first wanted. This trigger sits in the
     * site footer, so mounting the whole gate on every page would cost every page
     * that renders one. It stays mounted after the first open so closing keeps
     * its animation.
     */
    const [hasOpened, setHasOpened] = useState(false);
    const openModal = () => {
        setHasOpened(true);
        setOpen(true);
    };
    /**
     * Mount the modal on hover or focus, which starts its catalogue fetch before
     * the click. The options are a fixed handful and never change between the
     * hover and the open, so by the time the modal is on screen it usually has
     * them — and the reader sees the publications rather than a loading pane.
     */
    const prefetch = () => setHasOpened(true);

    return (
        <>
            {variant === 'footer' ? (
                <button
                    type="button"
                    onClick={openModal}
                    onPointerEnter={prefetch}
                    onFocus={prefetch}
                    className={cn(
                        // The footer field's own shape, kept exactly — only what
                        // sits inside it changes.
                        'flex w-full items-center justify-between rounded-full bg-white/5 px-6 max-md:h-[11.23vw] md:h-[54px] md:px-[35px]',
                        'transition-colors hover:bg-white/10',
                        className,
                    )}
                >
                    <span className="font-sans text-base font-medium tracking-[0.02em] text-white">
                        {label}
                    </span>
                    <ArrowRight
                        className="size-4 shrink-0 fill-white text-white"
                        aria-hidden="true"
                    />
                </button>
            ) : variant === 'link' ? (
                <button
                    type="button"
                    onClick={openModal}
                    onPointerEnter={prefetch}
                    onFocus={prefetch}
                    className={cn(
                        'text-[1.0625rem] font-medium tracking-[-0.02em] text-[#181818] underline underline-offset-4',
                        className,
                    )}
                >
                    {label}
                </button>
            ) : (
                <button
                    type="button"
                    onClick={openModal}
                    onPointerEnter={prefetch}
                    onFocus={prefetch}
                    className={cn(
                        'flex h-11 w-fit items-center gap-2 rounded-full bg-[#181818] px-5 font-sans text-base font-medium whitespace-nowrap text-white shadow-[0px_3.243px_11.351px_rgba(0,0,0,0.11)] transition-opacity hover:opacity-90',
                        className,
                    )}
                >
                    {label}
                    <ArrowRight
                        className="size-3.5 shrink-0 fill-white"
                        aria-hidden="true"
                    />
                </button>
            )}
            {hasOpened ? (
                <SubscribePublicationsModal
                    open={open}
                    onClose={() => setOpen(false)}
                    onSubscribed={onSubscribed}
                />
            ) : null}
        </>
    );
};
